import _ from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Allotment } from 'allotment'
import { skipToken } from '@reduxjs/toolkit/query'

import { DialogContent, DialogTitle } from '@mui/material'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import Portal from '@mui/material/Portal'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Biotech from '@mui/icons-material/Biotech'
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import PaymentIcon from '@mui/icons-material/Payment'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import AssignmentIcon from '@mui/icons-material/Assignment'

import ConsultationDocumentsIcon from '@/components/ui/icons/ConsultationDocuments'
import MedicalVideoIcon from '@/components/ui/icons/MedicalVideo'
import PatientIdentityIcon from '@/components/ui/icons/PatientIdentity'
import StethoscopeIcon from '@/components/ui/icons/Stethoscope'
import VitalParametersIcon from '@/components/ui/icons/VitalParameters'

import { BillingReviewStatus, ConsultationReadinessStatus, ConsultationStatus } from '@/core/models/consultation.model'
import { useGetConsultationQuery, useSetConsultationReadinessMutation } from '@/core/services/consultation'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import { useBillingDialogEnabled } from '@/hooks/useBillingDialogEnabled'
import { consultationApi } from '@/core/api/consultation'
import { getApiErrorMessage } from '@/utils/errors'
import { useSnackbar } from 'notistack'
import ConsultationPaymentDialog, { useConsultationPaymentDialog } from '@/pages/consultation/components/ConsultationPaymentDialog'
import { BillingButton, MarkReadyButton } from '@/pages/consultation-live/ui/Topbar'
import { ReadinessChecklistButton } from '@/pages/consultation-live/ui/ReadinessChecklistButton'
import { ConfirmReadyDialog } from '@/pages/consultation-live/ui/ConfirmReadyDialog'
import { useReadinessChecklist } from '@/pages/consultation-live/model/useReadinessChecklist'
import { usePatientConsentDialog } from '@/pages/consultation-live/model/usePatientConsentDialog'
import PatientConsentDialog from './components/PatientConsentDialog'

import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import useMedconnectDevices from '@/hooks/useMedconnectDevices'

import { useDiagnosticLayout } from '@/layouts/diagnostic/DiagnosticLayout'
import Sidebar from '@/layouts/diagnostic/Sidebar'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { confirmReadingMessages, selectUnreadMessagesByChatRoom } from '@/redux/reducers/chat-room.reducer'
import {
  getStunServer,
  getRpiMeetingId,
  joinConsultationSocketEvent,
  joinMeeting,
  leaveConsultationSocketEvent,
  selectAttendeeInfo,
  selectMeetingInfo,
  selectRpiMeetingId,
  selectTwilioStunServer,
  selectParticipantsByConsultationId,
} from '@/redux/reducers/consultation.reducer'
import { getCustomDevices, selectCustomDevices } from '@/redux/reducers/custom-device.reducer'
import { addUserToRoom, getRoom, mapRoomToConsultationIds, selectPeerId, selectRoomByConsultationId } from '@/redux/reducers/room.reducer'

import { getEnv } from '@/utils/helpers'
import { isStaff } from '@/utils/type-guards'

import Biologicals from './containers/Biologicals'
import DiagnosticMedicalVideo from './containers/DiagnosticMedicalVideo'
import DiagnosticStethoscope from './containers/DiagnosticStethoscope'
import Dictaphone from './containers/Dictaphone'
import Documents from './containers/Documents'
import EndLiveConsultationDialog from './containers/EndLiveConsultationDialog'
import InvitationDialog from './containers/InvitationDialog'
import MeetingClockComponent from './components/MeetingClock'
import Messenger from './containers/Messenger'
import NetworkSpeed from './containers/NetworkSpeed'
import PatientInfo from './containers/PatientInfo'
import Payment from './containers/Payment'
import RpiMesures from './containers/RpiMesures'
import TopbarButton from '../../layouts/diagnostic/TopbarButton'
import { AllDiagnostics } from './containers/AllInOneDiagnostics'
import { Visio } from './containers/Visio'
import ContentHeader from './components/ContentHeader'
import ConsultationForms from './containers/ConsultationForms'
import { GroupPatientsBar } from './components/GroupPatientsSidebar'
import { DiagnosticRTCProvider } from '@/providers/diagnosticRTC/DiagnosticRTCProvider'
import RpiProvider from '@/providers/rpi/RpiProvider'
import ReportLocalConsultation from './components/ReportLocalConsultation'
import { MedicalActsSection } from '@/features/medical-acts/ui/MedicalActsSection'
import { useMedicalActsLicenseEnabled } from '@/features/medical-acts/model/useMedicalActsLicenseEnabled'
import { useResizeDetector } from 'react-resize-detector'

export function GroupConsultationPage() {
  const dispatch = useAppDispatch()
  const { topbarContentContainer } = useDiagnosticLayout()
  const navigate = useNavigate()
  const { consultationId } = useParams()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { width: splitterWrapperWidth = 0, ref: splitterWrapperRef } = useResizeDetector()
  const isTooSmall = useIsMobile()
  const hasMedicalActsLicense = useMedicalActsLicenseEnabled()

  // Fetch the initial consultation to get groupId
  const { data: initialConsultation } = useGetConsultationQuery(consultationId ?? skipToken)

  // Fetch all group siblings
  const { data: groupConsultations } = consultationApi.useGetConsultationsByGroupQuery(
    initialConsultation?.groupId ?? skipToken
  )

  // Active patient consultation (switchable)
  const [activeConsultationId, setActiveConsultationId] = useState<string | undefined>(consultationId)

  // Fetch active consultation data
  const { data: consultation } = useGetConsultationQuery(activeConsultationId ?? skipToken)

  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false)
  const [endLiveConsultationDialogOpen, setEndLiveConsultationDialogOpen] = useState(false)
  const [messengerOpen, setMessengerOpen] = useState(false)
  const [openParticipantsDialog, setOpenParticipantsDialog] = useState(false)

  const authUser = useAppSelector(selectAuthUser)
  const isDoctor = useMemo(() => isStaff(authUser) && authUser?.status === 'MEDECIN', [authUser])
  const isPds = useMemo(() => isStaff(authUser) && authUser?.status === 'PDS', [authUser])
  const { enqueueSnackbar } = useSnackbar()

  // Room-level config (readiness signal + billing dialog), same gating as the single-patient live
  // page — group consultations share one waiting room.
  const { data: waitingRoom } = useGetWaitingRoomQuery(consultation?.waitingRoom ?? skipToken)

  // Operator (PDS) "mark ready": the backend fans the toggle out to every scheduled sibling, so
  // one click marks the whole group ready/preparing.
  const readinessEnabled = waitingRoom?.readinessConfig?.enabled ?? false
  const isActiveConsultationReady = consultation?.readiness?.status === ConsultationReadinessStatus.Ready
  const [setConsultationReadiness, { isLoading: isTogglingReadiness }] = useSetConsultationReadinessMutation()
  // Preparation checklist for the consultation being prepared (the active patient)
  const checklist = useReadinessChecklist({ consultation, waitingRoom })
  const [confirmReadyOpen, setConfirmReadyOpen] = useState(false)
  const doToggleReadiness = async () => {
    if (!activeConsultationId) return
    try {
      await setConsultationReadiness({ id: activeConsultationId, changes: { ready: !isActiveConsultationReady } }).unwrap()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.consultation.readiness.error')), { variant: 'error' })
    }
  }
  const handleToggleReadiness = async () => {
    // Soft confirm: marking ready before preparation is complete asks first, never blocks.
    // "Complete" = all boxes ticked (boolean template) or the form submitted (any field type).
    if (!isActiveConsultationReady && checklist.enabled && !checklist.isComplete) {
      setConfirmReadyOpen(true)
      return
    }
    await doToggleReadiness()
  }

  // Billing review dialog (per active patient), auto-shown when their consultation completes.
  const { open: paymentDialogOpen, consultationId: paymentDialogConsultationId, showDialog, autoShowDialog, hideDialog } = useConsultationPaymentDialog()
  const billingReviewPending = !consultation?.billingReview || consultation.billingReview.status === BillingReviewStatus.Pending
  const billingDialogEnabledForUser = useBillingDialogEnabled(waitingRoom)

  // Each patient is billed individually, so the exit warnings must look at the WHOLE group:
  // any completed sibling whose billing review is still pending blocks a clean exit.
  const pendingBillingConsultations = useMemo(
    () =>
      (groupConsultations ?? []).filter(
        (c) => c.status === ConsultationStatus.Completed && (!c.billingReview || c.billingReview.status === BillingReviewStatus.Pending)
      ),
    [groupConsultations]
  )
  const groupBillingPending = billingDialogEnabledForUser && pendingBillingConsultations.length > 0
  // Jump to the first unbilled patient: make them active and open their billing dialog.
  const handleReviewGroupBilling = () => {
    const first = pendingBillingConsultations[0]
    if (!first) return
    setEndLiveConsultationDialogOpen(false)
    setActiveConsultationId(first._id)
    showDialog(first._id)
  }

  useEffect(() => {
    if (!consultation || !activeConsultationId) return
    if (consultation.status !== ConsultationStatus.Completed) return
    if (!billingDialogEnabledForUser) return
    if (!billingReviewPending) return
    autoShowDialog(activeConsultationId)
  }, [consultation?.status, billingDialogEnabledForUser, billingReviewPending, activeConsultationId, autoShowDialog, consultation])

  // Browser-level guard: warn before closing the tab while ANY sibling's billing is still pending.
  useEffect(() => {
    if (!groupBillingPending) return
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [groupBillingPending])

  // Mandatory tele-consent (fr/it locales): one read-out consent for the group, recorded on every
  // sibling consultation. Declining navigates back home, as on the single-patient page.
  const consentDialog = usePatientConsentDialog({ consultation: initialConsultation })
  const customDevices = useAppSelector(selectCustomDevices)
  const medicalVideoAttendeeInfo = useAppSelector((state) => selectAttendeeInfo(state, 'medicalVideo'))
  const medicalVideoMeetingInfo = useAppSelector((state) => selectMeetingInfo(state, 'medicalVideo'))
  const peerId = useAppSelector(selectPeerId)
  const rpiMeetingId = useAppSelector(selectRpiMeetingId)
  // Room is linked to the first consultation in the group
  const room = useAppSelector((state) => (consultationId ? selectRoomByConsultationId(state, consultationId) : undefined))
  const stethoscopeAttendeeInfo = useAppSelector((state) => selectAttendeeInfo(state, 'stethoscope'))
  const stethoscopeMeetingInfo = useAppSelector((state) => selectMeetingInfo(state, 'stethoscope'))
  const twilioStunServer = useAppSelector(selectTwilioStunServer)
  const participants = useAppSelector((state) => (consultationId ? selectParticipantsByConsultationId(state, consultationId) : undefined))
  const unreadChatMessages = useAppSelector((state) => (consultation?.chatRoom ? selectUnreadMessagesByChatRoom(state, consultation.chatRoom) : []))

  const isConsultationActive = consultation?.status === 'active'

  const isStreamer = useMemo(() => {
    if (!authUser || !room) return false
    return authUser._id === (typeof room.creator === 'string' ? room.creator : room.creator._id)
  }, [authUser, room])

  const peerConfig = useMemo(
    () =>
      !twilioStunServer
        ? undefined
        : { iceServers: twilioStunServer.iceServers },
    [twilioStunServer]
  )

  useMedconnectDevices(isStaff(authUser) ? authUser.status === 'PDS' : false, activeConsultationId ?? '')

  const [isSidebarVisible, setSidebarVisible] = useState(true)
  const onSizeChange: any = (size: number[]) => {
    if (size[0] > splitterWrapperWidth / 5) setSidebarVisible(true)
    else setSidebarVisible(false)
  }

  useEffect(() => {
    if (!consultationId) return
    dispatch(getRoom(consultationId))
  }, [dispatch, consultationId])

  // Subscribe to every sibling's consultation socket room so consultation_updated events
  // (readiness, status, billing changes by other participants) reach this page live.
  const groupConsultationIds = useMemo(
    () => (groupConsultations?.length ? groupConsultations.map((c) => c._id) : consultationId ? [consultationId] : []),
    [groupConsultations, consultationId]
  )
  useEffect(() => {
    if (!groupConsultationIds.length) return
    groupConsultationIds.forEach((id) => dispatch(joinConsultationSocketEvent(id)))
    return () => {
      groupConsultationIds.forEach((id) => dispatch(leaveConsultationSocketEvent(id)))
    }
  }, [dispatch, groupConsultationIds])

  // Map the shared room to all group consultation IDs so containers can find it
  useEffect(() => {
    if (room && groupConsultations?.length) {
      dispatch(mapRoomToConsultationIds({ room, consultationIds: groupConsultations.map((c) => c._id) }))
    }
  }, [dispatch, room, groupConsultations])

  useEffect(() => {
    dispatch(getCustomDevices())
    dispatch(getStunServer())
  }, [dispatch])

  useEffect(() => {
    if (isStreamer) dispatch(getRpiMeetingId())
  }, [dispatch, isStreamer])

  useEffect(() => {
    if (consultationId && room && authUser?._id !== (typeof room.creator === 'string' ? room.creator : room.creator._id)) {
      dispatch(addUserToRoom(consultationId))
    }
  }, [authUser, consultationId, dispatch, room])

  useEffect(() => {
    if (!!consultation?.chatRoom && messengerOpen && !!unreadChatMessages.length) dispatch(confirmReadingMessages(consultation.chatRoom))
  }, [messengerOpen, unreadChatMessages, dispatch, consultation])

  const handleJoinMeeting = useCallback(() => {
    if (!consultationId || !authUser?._id) return
    dispatch(joinMeeting({ roomOrConsultationId: consultationId, userId: authUser._id, meetingType: 'visioConference' }))
  }, [dispatch, consultationId, authUser])

  const menuItems = useMemo(() => {
    const items = [
      { id: 'patientInfo', icon: PatientIdentityIcon, label: t('page.liveConsultation.sidebar.patientInfo.label') },
      { id: 'mesures', icon: VitalParametersIcon, label: t('page.liveConsultation.sidebar.mesures.label') },
      { id: 'documents', icon: ConsultationDocumentsIcon, label: t('page.liveConsultation.sidebar.documents.label') },
      { id: 'payment', icon: PaymentIcon, label: t('page.liveConsultation.sidebar.payment.label') },
      { id: 'transcriptions', icon: RecordVoiceOverIcon, label: 'transcriptions' },
      { id: 'forms', icon: AssignmentIcon, label: t('page.localConsultation.sidebar.forms.label') },
      { id: 'medicalActs', icon: LocalHospitalIcon, label: t('page.liveConsultation.sidebar.medicalActs.label', { defaultValue: 'Medical Acts' }) },
      { id: 'mesures', icon: VitalParametersIcon, label: t('page.localConsultation.sidebar.mesures.label'), isMenuDiagnostic: true },
      { id: 'medicalVideo', icon: MedicalVideoIcon, label: t('page.liveConsultation.sidebar.medicalVideo.label'), isMenuDiagnostic: true },
      { id: 'biologicals', icon: Biotech, label: t('page.localConsultation.sidebar.biologicals.label'), isMenuDiagnostic: true },
      { id: 'stethoscope', icon: StethoscopeIcon, label: t('page.localConsultation.sidebar.stethoscope.label'), isMenuDiagnostic: true },
    ]
    return items.filter((item) => {
      if (item.id === 'medicalActs') return hasMedicalActsLicense
      return true
    })
  }, [t, hasMedicalActsLicense])

  const [selectedMenuItem, setSelectedMenuItem] = useState<string>(() => {
    const menuItemParam = searchParams.get('menu_item')
    return (menuItems.find((mi) => mi.id === menuItemParam) || menuItems[0]).id
  })

  const handleSelectMenuItem = (id: string) => {
    if (id === 'documents') {
      setDocumentsDialogOpen(true)
      return
    }
    setSelectedMenuItem(id)
  }

  const toggleDocumentsDialog = () => setDocumentsDialogOpen(!documentsDialogOpen)
  const toggleEndLiveConsultationDialog = () => {
    setMessengerOpen(false)
    setEndLiveConsultationDialogOpen((prev) => !prev)
  }
  const toggleMessenger = () => setMessengerOpen((prev) => !prev)

  const handleCloseConsultation = () => toggleEndLiveConsultationDialog()

  if (!authUser || !consultation || !room) return null

  const peerjsConfig = {
    config: peerConfig,
    peerId: isStreamer && !!peerId ? _.replace(peerId, /mobile/g, '') : peerId,
    streamer: room.streamer,
  }

  const rpiConfig = !rpiMeetingId
    ? undefined
    : { meetingID: rpiMeetingId, serverUrl: getEnv('VITE_RPI_GATEWAY')! }

  return (
    <Stack direction="row" sx={{ height: '100%', overflow: 'hidden' }}>
      <Portal container={topbarContentContainer}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
              {t('page.groupConsultation.title')}
              {groupConsultations && ` (${groupConsultations.length} ${t('page.groupConsultation.patients')})`}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <MeetingClockComponent startDate={consultation.startDate} consultationStatus={ConsultationStatus.Active} />
            {!isTooSmall && <NetworkSpeed />}
            <Dictaphone consultationId={activeConsultationId} />
            <Tooltip title={t('layouts.diagnostic.header.openParticipantsButton.tooltip')}>
              <TopbarButton id="invite-button" onClick={() => setOpenParticipantsDialog(!openParticipantsDialog)} variant="contained">
                <PersonAddAltIcon />
              </TopbarButton>
            </Tooltip>
            <Badge badgeContent={unreadChatMessages.length} color="error" max={9} overlap="rectangular">
              <Tooltip title={t('page.liveConsultation.openMessengerButton.tooltip')}>
                <TopbarButton id="chat-button" onClick={toggleMessenger} variant="contained">
                  <ChatOutlinedIcon />
                </TopbarButton>
              </Tooltip>
            </Badge>
            {isPds && consultation?.waitingRoom && consultation.status === ConsultationStatus.Scheduled && readinessEnabled && (
              <>
                <ReadinessChecklistButton checklist={checklist} />
                <MarkReadyButton ready={isActiveConsultationReady} disabled={isTogglingReadiness} onClick={handleToggleReadiness} />
              </>
            )}
            {billingDialogEnabledForUser && consultation.status === ConsultationStatus.Completed && (
              <BillingButton billingReviewPending={billingReviewPending} onClick={() => activeConsultationId && showDialog(activeConsultationId)} />
            )}
            {/* The "!" badge mirrors the single-patient page's billing warning on the quit action */}
            <Badge badgeContent="!" color="warning" overlap="rectangular" invisible={!groupBillingPending}>
              <Button
                color="error"
                onClick={handleCloseConsultation}
                size="small"
                variant="contained"
                sx={{ borderRadius: '15px', fontSize: '12px' }}
              >
                {t(`page.liveConsultation.topbar.${isConsultationActive ? 'endConsultationButton' : 'quitConsultationButton'}.label`)}
              </Button>
            </Badge>
          </Stack>
        </Stack>
      </Portal>

      {/* Main content area */}
      <Box data-testid="splitter-wrapper" ref={splitterWrapperRef} sx={{ flex: 1, height: '100%' }}>
        <Allotment defaultSizes={[60, 40]} onChange={onSizeChange}>
          <Allotment.Pane minSize={0.5}>
            <Stack direction="row" sx={{ height: '100%' }}>
              <Sidebar menuItems={menuItems.filter((item) => !item.isMenuDiagnostic)} onSelect={handleSelectMenuItem} selectedMenuItem={selectedMenuItem} />
              {isSidebarVisible ? (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Group patient switcher bar */}
                  <GroupPatientsBar
                    consultations={groupConsultations || []}
                    activeConsultationId={activeConsultationId}
                    onSelectConsultation={setActiveConsultationId}
                  />
                  <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {selectedMenuItem === 'medicalVideo' && (
                    <DiagnosticRTCProvider
                      config={{
                        chime: { attendeeInfo: medicalVideoAttendeeInfo, meetingInfo: medicalVideoMeetingInfo },
                        peerjs: peerjsConfig,
                        rpi: rpiConfig,
                      }}
                      customDevices={customDevices}
                      isStreamer={isStreamer}
                      type="video"
                    >
                      <DiagnosticMedicalVideo consultationId={activeConsultationId} roomConsultationId={consultationId} />
                    </DiagnosticRTCProvider>
                  )}
                  {selectedMenuItem === 'mesures' && (
                    <>
                      {rpiMeetingId ? (
                        <RpiProvider isStreamer={isStreamer} meetingID={rpiMeetingId} serverUrl={getEnv('VITE_RPI_GATEWAY')!}>
                          <RpiMesures isStreamer={isStreamer} consultationId={activeConsultationId} />
                        </RpiProvider>
                      ) : (
                        <AllDiagnostics consultationId={activeConsultationId} roomConsultationId={consultationId} isStreamer={isStreamer} />
                      )}
                    </>
                  )}
                  {selectedMenuItem === 'biologicals' && <Biologicals consultationId={activeConsultationId} />}
                  {selectedMenuItem === 'patientInfo' && <PatientInfo consultationId={activeConsultationId} showHistory />}
                  {selectedMenuItem === 'payment' && <Payment consultationId={activeConsultationId} />}
                  {selectedMenuItem === 'transcriptions' && activeConsultationId && <ReportLocalConsultation consultationId={activeConsultationId} />}
                  {selectedMenuItem === 'forms' && (
                    <Box sx={{ p: 3 }}>
                      <ConsultationForms consultationId={activeConsultationId!} />
                    </Box>
                  )}
                  {selectedMenuItem === 'medicalActs' && (
                    <Box sx={{ p: 3 }}>
                      <MedicalActsSection patientId={consultation.patient._id} consultationId={activeConsultationId} />
                    </Box>
                  )}
                  {selectedMenuItem === 'stethoscope' && (
                    <DiagnosticRTCProvider
                      config={{
                        chime: { attendeeInfo: stethoscopeAttendeeInfo, meetingInfo: stethoscopeMeetingInfo },
                        peerjs: { ...peerjsConfig, streamer: _.replace(room.streamer, /mobile/g, '') },
                        rpi: rpiConfig,
                      }}
                      customDevices={customDevices}
                      isStreamer={isStreamer}
                      type="audio"
                    >
                      <DiagnosticStethoscope consultationId={activeConsultationId} roomConsultationId={consultationId} />
                    </DiagnosticRTCProvider>
                  )}
                  </Box>
                </Box>
              ) : (
                <ContentHeader sx={{ p: '20px 16px' }} title={t('page.liveConsultation.consultationInfo.title')} />
              )}
            </Stack>
          </Allotment.Pane>
          <Allotment.Pane minSize={0.5}>
            <Visio consultation={consultation} handleJoinMeeting={handleJoinMeeting} />
          </Allotment.Pane>
        </Allotment>
      </Box>

      <Dialog fullWidth maxWidth="md" open={documentsDialogOpen} onClose={toggleDocumentsDialog}>
        <Documents consultationId={activeConsultationId} />
      </Dialog>
      <InvitationDialog open={openParticipantsDialog} consultation={consultation} onClose={() => setOpenParticipantsDialog(false)} />
      <EndLiveConsultationDialog
        onClose={toggleEndLiveConsultationDialog}
        open={endLiveConsultationDialogOpen}
        billingReviewPending={groupBillingPending}
        pendingBillingPatientCount={pendingBillingConsultations.length}
        onReviewBilling={handleReviewGroupBilling}
      />
      <Messenger chatRoomId={consultation.chatRoom} onClose={toggleMessenger} open={messengerOpen} />
      <ConsultationPaymentDialog open={paymentDialogOpen} consultationId={paymentDialogConsultationId} onClose={hideDialog} consultation={consultation} />
      <ConfirmReadyDialog
        open={confirmReadyOpen}
        uncheckedCount={checklist.uncheckedCount}
        onConfirm={doToggleReadiness}
        onClose={() => setConfirmReadyOpen(false)}
      />
      {consentDialog.shouldShowConsentDialog && (
        <PatientConsentDialog
          open={consentDialog.isConsentDialogOpen}
          onClose={consentDialog.handleConsentResponse}
          consultationId={consultationId}
          consultationIds={groupConsultationIds}
          showTreatingPhysician={consentDialog.showTreatingPhysician}
        />
      )}
    </Stack>
  )
}

export default GroupConsultationPage
